import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeart, 
  faHandHoldingHeart,
  faHistory,
  faCheckCircle,
  faSpinner,
  faExclamationTriangle,
  faInfoCircle,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { donationsAPI } from '../services/api';
import paystackService from '../services/paystack.service';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Donations.css';

const Donations = () => {
  const navigate = useNavigate();
  const [causes, setCauses] = useState([]);
  const [selectedCause, setSelectedCause] = useState(null);
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [totalDonated, setTotalDonated] = useState(0);

  const presetAmounts = paystackService.getPresetAmounts();

  // Helper to check if button should be enabled - memoized to recalculate when dependencies change
  const isDonateEnabled = useMemo(() => {
    const numericAmount =
  Number(customAmount) > 0
    ? Number(customAmount)
    : Number(amount);

const hasValidAmount = numericAmount > 0;
    const isEnabled = !loading && !!selectedCause && hasValidAmount;
    
    console.log('Donate button check:', {
      loading,
      selectedCause,
      amount,
      customAmount,
      hasValidAmount,
      causesCount: causes.length,
      isEnabled
    });
    
    return isEnabled;
  }, [loading, selectedCause, amount, customAmount, causes.length]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setInitialLoading(true);
      
      const [causesRes, historyRes] = await Promise.all([
        donationsAPI.getCauses().catch(() => ({ data: { causes: [] } })),
        donationsAPI.getHistory().catch(() => ({ data: { donations: [] } }))
      ]);

      const fetchedCauses = causesRes.data?.causes || [];
      const fetchedHistory = historyRes.data?.donations || [];

      setCauses(fetchedCauses);
      setHistory(fetchedHistory);
      
      const total = fetchedHistory.reduce(
        (sum, d) => sum + parseFloat(d.amount || 0), 
        0
      );
      setTotalDonated(total);

      if (fetchedCauses.length > 0) {
        setSelectedCause(fetchedCauses[0].id);
      }
    } catch (error) {
      console.error('Error fetching donation data:', error);
      toast.error('Failed to load donation information');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleDonate = async (e) => {
    e.preventDefault();
    
    const donationAmount = customAmount || amount;
    
    // Validate amount
    const validation = paystackService.validateDonationAmount(donationAmount);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    if (!selectedCause) {
      toast.error('Please select a cause');
      return;
    }

    const selectedCauseData = causes.find(c => c.id === selectedCause);
    if (!selectedCauseData) {
      toast.error('Invalid cause selected');
      return;
    }

    setLoading(true);

    try {
      // Process payment through Paystack
      await paystackService.processDonation(
        {
          amount: parseFloat(donationAmount),
          causeId: selectedCause,
          causeName: selectedCauseData.name,
        },
        // Success callback
        async (result) => {
          console.log('Payment successful:', result);
          
          try {
            // Save donation to backend
            const saveResponse = await donationsAPI.makeDonation({
              cause_id: selectedCause,
              amount: parseFloat(donationAmount),
              payment_reference: result.reference,
              payment_status: 'completed'
            });
            
            console.log('Donation saved successfully:', saveResponse.data);
            
            // Show success message
            toast.success(
              `Thank you for your ${paystackService.formatCurrency(donationAmount)} donation to ${selectedCauseData.name}!`,
              { autoClose: 5000 }
            );
            
            // Reset form
            setAmount('');
            setCustomAmount('');
            
            // Refresh data to show new donation in history
            await fetchData();
            
          } catch (saveError) {
            console.error('Error saving donation to database:', saveError);
            
            // Payment successful but save failed - critical error
            toast.error(
              'Payment was successful but we couldn\'t record your donation. ' +
              'Please save this reference: ' + result.reference + ' and contact support.',
              { autoClose: 10000 }
            );
          } finally {
            setLoading(false);
          }
        },
        // Error callback
        (error) => {
          console.error('Payment/Donation error:', error);
          toast.error(error.message || 'Donation failed. Please try again.');
          setLoading(false);
        }
      );
    } catch (error) {
      console.error('Payment initialization error:', error);
      toast.error(error.message || 'Failed to initialize payment. Please try again.');
      setLoading(false);
    }
  };

  const getSelectedCause = () => {
    return causes.find(c => c.id === selectedCause);
  };

  if (initialLoading) {
    return (
      <div className="donations-page">
        <div className="donations-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          <p>Loading donations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="donations-page">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Back to Dashboard Button */}
      <button 
        onClick={() => navigate('/dashboard')} 
        className="back-to-dashboard"
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
      </button>
      
      {/* Hero Section */}
      <div className="donations-hero">
        <div className="hero-icon">
          <FontAwesomeIcon icon={faHeart} />
        </div>
        <h1>Support Causes That Matter</h1>
        <p>Your donations help make a real difference in people's lives</p>
      </div>

      {/* Impact Stats */}
      {totalDonated > 0 && (
        <div className="impact-stats">
          <div className="stat-item">
            <div className="stat-value">{paystackService.formatCurrency(totalDonated)}</div>
            <div className="stat-label">Total Donated</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <div className="stat-value">{history.length}</div>
            <div className="stat-label">Donations Made</div>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item">
            <FontAwesomeIcon icon={faHeart} className="stat-icon" />
            <div className="stat-label">Thank You!</div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="donations-content">
        {/* Donation Form Section */}
        <div className="donation-form-section">
          <div className="section-header">
            <FontAwesomeIcon icon={faHandHoldingHeart} />
            <h2>Make a Donation</h2>
          </div>

          <form onSubmit={handleDonate} className="donation-form">
            {/* Select Cause */}
            {causes.length > 0 ? (
              <div className="form-section">
                <label className="section-label">Choose a Cause</label>
                <div className="causes-list">
                  {causes.map((cause) => (
                    <div
                      key={cause.id}
                      className={`cause-option ${selectedCause === cause.id ? 'selected' : ''}`}
                      onClick={() => setSelectedCause(cause.id)}
                    >
                      <div className="cause-header">
                        <h3>{cause.name}</h3>
                        {selectedCause === cause.id && (
                          <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                        )}
                      </div>
                      <p className="cause-description">{cause.description}</p>
                      {cause.goal_amount && (
                        <div className="cause-goal">
                          <div className="goal-bar">
                            <div 
                              className="goal-progress"
                              style={{ 
                                width: `${Math.min((cause.current_amount / cause.goal_amount) * 100, 100)}%` 
                              }}
                            ></div>
                          </div>
                          <div className="goal-text">
                            <span>{paystackService.formatCurrency(cause.current_amount)}</span>
                            <span className="goal-target">of {paystackService.formatCurrency(cause.goal_amount)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-causes">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <p>No active causes available at the moment.</p>
                <small>Please check back later.</small>
              </div>
            )}

            {/* Amount Selection */}
            <div className="form-section">
              <label className="section-label">Select Amount</label>
              <div className="amount-grid">
                {presetAmounts.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`amount-option ${amount === preset ? 'selected' : ''}`}
                    onClick={() => {
                      setAmount(Number(preset));
                      setCustomAmount('');
                    }}
                  >
                    <span className="amount-value">{paystackService.formatCurrency(preset)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount */}
            <div className="form-section">
              <label className="section-label">Or Enter Custom Amount</label>
              <div className="custom-amount-input">
                <span className="currency-symbol">₦</span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(Number(e.target.value));
                    setAmount('');
                  }}
                  placeholder={amount ? `Selected: ${paystackService.formatCurrency(amount)}` : 'Enter amount'}
                  min={paystackService.getMinAmount()}
                  max={paystackService.getMaxAmount()}
                  step="1"
                />
              </div>
              <div className="amount-hint">
                <FontAwesomeIcon icon={faInfoCircle} />
                <span>
                  Min: {paystackService.formatCurrency(paystackService.getMinAmount())} • 
                  Max: {paystackService.formatCurrency(paystackService.getMaxAmount())}
                </span>
              </div>
              {amount && !customAmount && (
                <div className="selected-amount-display">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Selected: {paystackService.formatCurrency(amount)}</span>
                </div>
              )}
            </div>

            {/* Donation Summary */}
            {selectedCause && (customAmount || amount) && (
              <div className="donation-summary">
                <h4>Donation Summary</h4>
                <div className="summary-item">
                  <span>Cause:</span>
                  <strong>{getSelectedCause()?.name}</strong>
                </div>
                <div className="summary-item total">
                  <span>Amount:</span>
                  <strong>{paystackService.formatCurrency(customAmount || amount)}</strong>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button 
              type="submit" 
              className="donate-button"
              disabled={!isDonateEnabled}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Processing Payment...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faHeart} />
                  <span>Donate {paystackService.formatCurrency(customAmount || amount || 0)}</span>
                </>
              )}
            </button>

            <div className="payment-security">
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>Secured by Paystack • Your payment information is encrypted</span>
            </div>
          </form>
        </div>

        {/* Sidebar */}
        <div className="donations-sidebar">
          {/* Why Donate */}
          <div className="sidebar-card">
            <h3>Why Your Support Matters</h3>
            <div className="benefits-list">
              <div className="benefit">
                <FontAwesomeIcon icon={faHeart} />
                <div>
                  <h4>Make an Impact</h4>
                  <p>Your donations directly support important causes</p>
                </div>
              </div>
              <div className="benefit">
                <FontAwesomeIcon icon={faHistory} />
                <div>
                  <h4>Track Your Giving</h4>
                  <p>See your donation history and impact</p>
                </div>
              </div>
              <div className="benefit">
                <FontAwesomeIcon icon={faCheckCircle} />
                <div>
                  <h4>Secure & Transparent</h4>
                  <p>All donations processed securely</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Donation History */}
      {history.length > 0 && (
        <div className="donations-history">
          <div className="history-header">
            <FontAwesomeIcon icon={faHistory} />
            <h2>Your Donation History</h2>
          </div>
          <div className="history-list">
            {history.map((donation) => (
              <div key={donation.id} className="history-item">
                <div className="history-main">
                  <div className="history-cause">{donation.cause_name}</div>
                  <div className="history-amount">{paystackService.formatCurrency(parseFloat(donation.amount))}</div>
                </div>
                <div className="history-meta">
                  <div className="history-date">
                    {new Date(donation.created_at).toLocaleDateString('en-NG', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                  <div className="history-status">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Completed</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Donations;
